import { useEffect, useState, type FormEvent } from 'react';
import { useAdminEvent } from '@/app/admin/AdminEventContext';
import { adminApi } from '@/features/admin/api/adminApi';
import { ResetPasswordForm } from '@/features/admin/users/components/ResetPasswordForm';
import { Button } from '@/shared/components/Button';
import { Spinner } from '@/shared/components/Spinner';
import { EmptyState } from '@/shared/components/EmptyState';
import { Pill } from '@/shared/components/Pill';
import { ApiError } from '@/shared/api/httpClient';
import type { ExhibitorDto, UserDto } from '@/shared/types/domain';

export function UsersPage() {
  const { eventId, isLoading: isEventLoading } = useAdminEvent();
  const [users, setUsers] = useState<UserDto[]>([]);
  const [exhibitors, setExhibitors] = useState<ExhibitorDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [resettingId, setResettingId] = useState<string | null>(null);

  function refetch() {
    if (!eventId) return;
    setIsLoading(true);
    Promise.all([adminApi.users.listByEvent(eventId), adminApi.exhibitors.listByEvent(eventId)])
      .then(([u, ex]) => {
        setUsers(u);
        setExhibitors(ex);
      })
      .finally(() => setIsLoading(false));
  }

  useEffect(refetch, [eventId]);

  if (isEventLoading) return <Spinner label="Loading" />;
  if (!eventId) return <EmptyState icon="◧" title="No events yet" description="Create an event first." />;

  async function toggleActive(user: UserDto) {
    const updated = await adminApi.users.update(user.id, {
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role as 'ExhibitorAdmin' | 'ExhibitorStaff',
      isActive: !user.isActive
    });
    setUsers((current) => current.map((u) => (u.id === updated.id ? updated : u)));
  }

  return (
    <div>
      <div className="admin-page__header">
        <p style={{ color: 'var(--text-muted)', margin: 0 }}>
          {users.length} staff account{users.length === 1 ? '' : 's'}
        </p>
        <Button onClick={() => setShowCreate((v) => !v)} disabled={exhibitors.length === 0}>
          {showCreate ? 'Cancel' : '+ New user'}
        </Button>
      </div>

      {exhibitors.length === 0 && (
        <EmptyState icon="▦" title="Add an exhibitor first" description="Staff accounts must belong to an exhibitor." />
      )}

      {showCreate && (
        <CreateUserForm
          exhibitors={exhibitors}
          onCreated={() => {
            setShowCreate(false);
            refetch();
          }}
        />
      )}

      {isLoading ? (
        <Spinner label="Loading users" />
      ) : users.length === 0 && exhibitors.length > 0 ? (
        <EmptyState icon="◎" title="No staff accounts yet" description="Create the first one above." />
      ) : (
        users.length > 0 && (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Exhibitor</th>
                <th>Role</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <>
                  <tr key={u.id}>
                    <td>
                      {u.firstName} {u.lastName}
                    </td>
                    <td>{u.email}</td>
                    <td>{u.exhibitorName}</td>
                    <td>{u.role}</td>
                    <td>
                      <Pill tone={u.isActive ? 'online' : 'offline'}>{u.isActive ? 'Active' : 'Disabled'}</Pill>
                    </td>
                    <td style={{ display: 'flex', gap: 'var(--space-3)' }}>
                      <button
                        type="button"
                        onClick={() => void toggleActive(u)}
                        style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: '0.875rem' }}
                      >
                        {u.isActive ? 'Disable' : 'Enable'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setResettingId(resettingId === u.id ? null : u.id)}
                        style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: '0.875rem' }}
                      >
                        {resettingId === u.id ? 'Close' : 'Reset password'}
                      </button>
                    </td>
                  </tr>
                  {resettingId === u.id && (
                    <tr key={`${u.id}-reset`}>
                      <td colSpan={6} style={{ background: 'var(--bg)' }}>
                        <ResetPasswordForm userId={u.id} onDone={() => setResettingId(null)} />
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        )
      )}
    </div>
  );
}

function CreateUserForm({ exhibitors, onCreated }: { exhibitors: ExhibitorDto[]; onCreated: () => void }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'ExhibitorAdmin' | 'ExhibitorStaff'>('ExhibitorStaff');
  const [exhibitorId, setExhibitorId] = useState(exhibitors[0]?.id ?? '');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setIsSaving(true);

    try {
      await adminApi.users.create({ email, firstName, lastName, password, role, exhibitorId });
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.userMessage : 'Could not create user.');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 'var(--space-4)',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-5)',
        marginBottom: 'var(--space-5)'
      }}
    >
      {error && <div className="error-banner" style={{ gridColumn: '1 / -1' }}>{error}</div>}

      <div className="field">
        <label className="field__label">First name</label>
        <input className="field__input" required value={firstName} onChange={(e) => setFirstName(e.target.value)} />
      </div>
      <div className="field">
        <label className="field__label">Last name</label>
        <input className="field__input" required value={lastName} onChange={(e) => setLastName(e.target.value)} />
      </div>
      <div className="field">
        <label className="field__label">Email</label>
        <input type="email" className="field__input" required value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div className="field">
        <label className="field__label">Temporary password</label>
        <input
          type="text"
          className="field__input"
          required
          minLength={10}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>
      <div className="field">
        <label className="field__label">Exhibitor</label>
        <select className="field__input" value={exhibitorId} onChange={(e) => setExhibitorId(e.target.value)}>
          {exhibitors.map((ex) => (
            <option key={ex.id} value={ex.id}>
              {ex.companyName}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label className="field__label">Role</label>
        <select className="field__input" value={role} onChange={(e) => setRole(e.target.value as typeof role)}>
          <option value="ExhibitorStaff">Exhibitor Staff</option>
          <option value="ExhibitorAdmin">Exhibitor Admin</option>
        </select>
      </div>

      <div style={{ gridColumn: '1 / -1' }}>
        <Button type="submit" isLoading={isSaving}>
          Create account
        </Button>
      </div>
    </form>
  );
}